<template>
  <div class="account-infos">
    <div class="_company-image">
      <div class="_avatar">
        <img
          v-if="imageUrl !== ''"
          :src="imageUrl"
          :alt="`${companyNameField.value} Logo`"
        >
        <div
          v-else
          class="_img-placeholder"
        >
          <div class="_text">
            {{ companyNameField.value === '' ? 'M' : companyNameField.value.charAt(0) }}
          </div>
        </div>
        <input
          ref="fileinput"
          type="file"
          :style="{display: 'none'}"
          accept="image/*"
          @change="fileChanged"
        >
        <m-dialog
          v-model:value="showCropImage"
          :title="t('accountSettings.setCompanyPicture')"
          hide-footer
          keep-open-on-mask-click
          no-padding
        >
          <image-crop
            v-if="file !== null"
            class="_image-crop"
            :file="file"
            @cropped="onCropped"
            @cancel="closeCrop"
            @wrong-file-type="handleWrongFileType"
          />
        </m-dialog>
      </div>
      <div class="_upload-btn">
        <m-btn
          hide-border
          small
          light
          @click="triggerInput"
        >
          {{ t('accountInfos.uploadImage') }}
        </m-btn>
      </div>
    </div>
    <div class="_form">
      <div class="_item">
        <div class="_label">
          {{ t('accountInfos.companyNameLabel') }}
        </div>
        <m-tooltip
          :force-show="companyNameField.hasError"
          :relocate-key="companyNameField.value"
          placement="left"
        >
          <m-text-field
            v-model:value="companyNameField.value"
            :has-error="companyNameField.hasError"
            auto-focus
            @blur="companyNameField.onBlur"
            @keydown.enter="submit"
          />
          <template
            v-if="companyNameField.hasError"
            #title
          >
            {{ companyNameField.error }}
          </template>
        </m-tooltip>
      </div>
      <div class="_item">
        <div class="_label">
          {{ t('accountInfos.companySize') }}
        </div>
        <m-select
          v-model:value="companySize"
          :items="items"
          full-width
          match-trigger-width
          :style="{ background: 'white' }"
        />
      </div>
      <m-btn
        color="black"
        :disabled="disabled"
        block
        class="_submit"
        @click="submit"
      >
        {{ submitLabel }}
      </m-btn>
    </div>
  </div>
</template>

<script setup>
import ImageCrop from '@/components/ImageCrop.vue';
import useSnackBar from '@/composables/snackbar';
import { computed, ref, watch } from 'vue';
import { useCompanyNameField } from '@/composables/field-validation';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  initialCompanyName: {
    type: String,
    default: '',
  },
  initialCompanySize: {
    type: String,
    default: null,
  },
  imageUrl: {
    type: String,
    default: '',
  },
  submitLabel: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['image-cropped', 'submit']);

const { t } = useI18n();
const snackbar = useSnackBar();

const companyNameField = useCompanyNameField(ref(props.initialCompanyName));
const companySize = ref(props.initialCompanySize);
const file = ref(null);
const showCropImage = ref(false);
const fileinput = ref(null);

const items = [
  { text: '1-50', value: '1-20' },
  { text: '51-100', value: '21-100' },
  { text: '101-500', value: '101-500' },
  { text: '501-1000', value: '501-1000' },
  { text: '1001-5000', value: '1001-5000' },
  { text: '5000+', value: '5000+' },
];

const disabled = computed(() => !companyNameField.isValid || companySize.value === null);

const submit = () => {
  companyNameField.onSubmit();
  if (disabled.value) {
    return;
  }
  emit('submit', { companyName: companyNameField.value, companySize: companySize.value });
};

const triggerInput = () => fileinput.value.click();

const closeCrop = () => { showCropImage.value = false; };

const onCropped = (blob) => {
  closeCrop();
  emit('image-cropped', blob);
};

const handleWrongFileType = () => {
  closeCrop();
  snackbar.error(t('error.notAnImage'));
};

const fileChanged = (event) => {
  if (event.target.files.length === 0) {
    return;
  }

  file.value = event.target.files[0];
  showCropImage.value = true;
  fileinput.value.value = '';
};

watch(showCropImage, (newValue) => {
  if (!newValue) {
    file.value = null;
  }
});
</script>

<style scoped lang="scss" type="text/scss">
  @import "shared/assets/scss/box-shadow";

  .account-infos {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    ._company-image {
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin-bottom: 2rem;

      ._avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1.6rem;
        text-align: center;

        img {
          width: 6.4rem;
          height: 6.4rem;
          border-radius: .4rem;

          @include box_shadow(1);
        }

        ._img-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 6.4rem;
          height: 6.4rem;
          background-color: map_get($grey, 'darken-1');
          border-radius: .4rem;

          ._text {
            font-size: $font-size-8;
            color: white;
          }
        }
      }
    }

    ._form {
      width: 100%;
      max-width: 28rem;

      ._item {
        margin-bottom: 1.6rem;

        ._label {
          font-size: $font-size-3;
          color: $font-color-secondary;
        }
      }
    }

  }
</style>
